/**
 * Floating Lights Effect Component
 * Diya/candle light animation for Diwali and festive themes
 */

import { useEffect, useRef } from 'react'

interface FloatingLightsProps {
  intensity?: string
}

interface Light {
  x: number
  y: number
  radius: number
  glowRadius: number
  speed: number
  opacity: number
  flicker: number
}

export function FloatingLights({ intensity = 'medium' }: FloatingLightsProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number>()
  const lightsRef = useRef<Light[]>([])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Intensity settings
    const intensitySettings = {
      low: { count: 15, minRadius: 2, maxRadius: 4 },
      medium: { count: 25, minRadius: 3, maxRadius: 5 },
      high: { count: 40, minRadius: 4, maxRadius: 6 },
    }

    const settings = intensitySettings[intensity as keyof typeof intensitySettings] || intensitySettings.medium

    // Initialize lights
    const initLights = () => {
      lightsRef.current = []
      for (let i = 0; i < settings.count; i++) {
        lightsRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * (settings.maxRadius - settings.minRadius) + settings.minRadius,
          glowRadius: (Math.random() * (settings.maxRadius - settings.minRadius) + settings.minRadius) * 3,
          speed: Math.random() * 0.3 + 0.1,
          opacity: Math.random() * 0.3 + 0.7,
          flicker: Math.random() * Math.PI * 2,
        })
      }
    }

    initLights()

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      lightsRef.current.forEach((light) => {
        // Update position (floating upward)
        light.y -= light.speed
        light.flicker += 0.1

        // Reset if off screen
        if (light.y < -20) {
          light.y = canvas.height + 20
          light.x = Math.random() * canvas.width
        }

        // Flicker effect
        const flickerOpacity = light.opacity + Math.sin(light.flicker) * 0.2

        // Draw glow (outer)
        const gradient = ctx.createRadialGradient(light.x, light.y, 0, light.x, light.y, light.glowRadius)
        gradient.addColorStop(0, `rgba(255, 165, 0, ${flickerOpacity * 0.4})`) // Orange
        gradient.addColorStop(0.5, `rgba(255, 200, 0, ${flickerOpacity * 0.2})`) // Gold
        gradient.addColorStop(1, 'rgba(255, 165, 0, 0)')

        ctx.beginPath()
        ctx.arc(light.x, light.y, light.glowRadius, 0, Math.PI * 2)
        ctx.fillStyle = gradient
        ctx.fill()

        // Draw light (inner)
        ctx.beginPath()
        ctx.arc(light.x, light.y, light.radius, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(255, 200, 0, ${flickerOpacity})` // Gold
        ctx.fill()
      })

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [intensity])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0, // Behind content but above background
      }}
      aria-hidden="true"
    />
  )
}

